import type * as React from 'react'

// Epic 52 sample component — proves the icon normalisation contract
// (.claude/epics/52-token-and-icon-foundation/reference/ICON-NORMALISATION.md §(b)/(c))
// on `icon / Copy` (16px export at node 145:18218; 12px export at node 145:18223).
//
// NOTE (Phase 00 contract gap, still open): the contract's §(c) specifies a shared
// `SvgIconProps` type at `apps/web/src/components/icons/types.ts`. That file does not exist yet
// and no phase currently owns creating it (see PHASE-00 Notes, "Open gap, not this phase's to
// resolve"). This component must not create that shared module unprompted, so the prop type is
// inlined here rather than imported. Reconcile with `./types` once a later phase adds it.
type SvgIconProps = Omit<React.SVGProps<SVGSVGElement>, 'children'> & {
  /** Accessible name. Omit for a purely decorative icon (default: aria-hidden). */
  title?: string
  /** Rendered size in px. Defaults to 16 — the artwork's native/authored size. */
  /** 24 added 2026-08-31: the timeline empty state's 24px glyph (270:30523 "Copy") is an
   *  exact 1.5x scale of this same path, so it reuses this component rather than
   *  shipping a second copy of the artwork. */
  size?: 12 | 16 | 24
}

// D-ICON-1 (2026-08-25): the 12px and 16px `Copy` exports were measured coordinate-by-coordinate
// (66 coordinates compared) and found to be a uniform 4/3 scale of one another, ratio spread
// 0.000009 — equal to within float rounding. Per the owner decision this single component keeps
// the 16px artwork and viewBox (more coordinate precision than the 12px export) and lets the
// `size` prop drive only the rendered width/height attributes; the viewBox stays "0 0 16 16"
// regardless of `size`, so the 12px render is the SVG user agent uniformly scaling this same
// path data by 12/16 — mathematically identical to what the separate 12px artwork was (the two
// differed only by that same uniform scale).
export function IconCopy({ title, size = 16, ...rest }: SvgIconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden={title ? undefined : true}
      role={title ? 'img' : undefined}
      {...rest}
    >
      {title ? <title>{title}</title> : null}
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M13.5 1.75C13.9142 1.75 14.25 2.08579 14.25 2.5V10.5C14.25 10.9142 13.9142 11.25 13.5 11.25H11.25V13.5C11.25 13.9142 10.9142 14.25 10.5 14.25H2.5C2.08579 14.25 1.75 13.9142 1.75 13.5V5.5C1.75 5.08579 2.08579 4.75 2.5 4.75H4.75V2.5C4.75 2.08579 5.08579 1.75 5.5 1.75H13.5ZM3.25 12.75H9.75V6.25H3.25V12.75ZM6.25 4.75H10.5C10.9142 4.75 11.25 5.08579 11.25 5.5V9.75H12.75V3.25H6.25V4.75Z"
        fill="currentColor"
      />
    </svg>
  )
}
